/**
 * Determines the upload action for an item based on its local and remote state.
 *
 * @param localItem - The local item with deletedAt and syncedAt fields.
 * @param remoteExists - Whether the item exists on the remote server.
 * @returns The action to take: 'delete-remote', 'skip', 'delete-local', 'insert', 'update', or 'mark-synced'.
 */
export function resolveUploadAction(
  localItem: { deletedAt: number | null; syncedAt: number | null; updatedAt: number },
  remoteExists: boolean,
  remoteUpdatedAt: number | null
): 'delete-remote' | 'skip' | 'delete-local' | 'insert' | 'update' | 'mark-synced' {
  // Locally deleted item
  if (localItem.deletedAt !== null) {
    return remoteExists ? 'delete-remote' : 'skip'
  }

  // Item doesn't exist remotely
  if (!remoteExists) {
    // Previously synced → was deleted remotely by another device
    if (localItem.syncedAt !== null) {
      return 'delete-local'
    }
    // Never synced → new item, upload it
    return 'insert'
  }

  // Both exist: last-write-wins
  if (remoteUpdatedAt !== null && localItem.updatedAt > remoteUpdatedAt) {
    return 'update'
  }

  return 'mark-synced'
}

/**
 * Determines whether an item should be included in the sync queue.
 * Includes soft-deleted items so that offline deletions are propagated.
 *
 * @returns true for all items (deleted or not).
 */
export function shouldQueueForSync(_item: { deletedAt: number | null }): boolean {
  return true
}
